/**
 * Beta Feedback Survey — public link for testers.
 * Route: /feedback
 *
 * Designed to be completable in 2–3 minutes. Single scroll, lots of breathing
 * room, every interactive element has a data-testid.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Check, Image as ImageIcon, X } from 'lucide-react';
import api from '../lib/api';

const ACTIONS = [
  { key: 'create_plan',   label: 'Create a plan' },
  { key: 'join_plan',     label: 'Join a plan' },
  { key: 'post_story',    label: 'Post a story' },
  { key: 'send_message',  label: 'Send a message' },
  { key: 'follow_someone',label: 'Follow someone' },
  { key: 'explore_plans', label: 'Explore plans' },
  { key: 'none',          label: 'None of the above' },
];

const FEATURES = [
  { key: 'plans',              label: 'Plans' },
  { key: 'stories',            label: 'Stories' },
  { key: 'chat',               label: 'Chat' },
  { key: 'explore',            label: 'Explore' },
  { key: 'profiles',           label: 'Profiles' },
  { key: 'reputation_levels',  label: 'Reputation & Levels' },
  { key: 'other',              label: 'Other' },
];

const USE_AGAIN = [
  { key: 'definitely_not',  label: 'Definitely not',  emoji: '😬' },
  { key: 'probably_not',    label: 'Probably not',    emoji: '😕' },
  { key: 'maybe',           label: 'Maybe',           emoji: '🤔' },
  { key: 'probably_yes',    label: 'Probably yes',    emoji: '🙂' },
  { key: 'definitely_yes',  label: 'Definitely yes',  emoji: '🤩' },
];

export default function BetaFeedback() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // form state
  const [ease, setEase] = useState(0);
  const [whatFor, setWhatFor] = useState('');
  const [actions, setActions] = useState([]);
  const [confusing, setConfusing] = useState('');
  const [hadBugs, setHadBugs] = useState(null); // true | false | null
  const [bugDesc, setBugDesc] = useState('');
  const [bugScreenshotUrl, setBugScreenshotUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [favorite, setFavorite] = useState('');
  const [favOther, setFavOther] = useState('');
  const [improve, setImprove] = useState('');
  const [useAgain, setUseAgain] = useState('');
  const [nps, setNps] = useState(null);
  const [finalThoughts, setFinalThoughts] = useState('');
  const [followUp, setFollowUp] = useState(null);
  const [followUpContact, setFollowUpContact] = useState('');

  // submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/feedback/beta/mine');
        if (data.submitted) {
          setAlreadySubmitted(true);
          setSubmitted(true);
        }
      } catch (e) {
        // /mine returns 401 for anonymous, that's expected for a public link.
        if (e?.response?.status && e.response.status !== 401) {
          // Don't block the form, but at least let the bug be visible in console.
          // eslint-disable-next-line no-console
          console.warn('Beta feedback prefill failed:', e);
        }
      }
    })();
  }, []);

  const toggleAction = (key) => {
    setActions(prev => {
      if (key === 'none') return prev.includes('none') ? [] : ['none'];
      const without_none = prev.filter(a => a !== 'none');
      return without_none.includes(key)
        ? without_none.filter(a => a !== key)
        : [...without_none, key];
    });
  };

  const handleScreenshot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/media/upload?kind=feedback', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBugScreenshotUrl(data.url);
    } catch (err) {
      setError('Upload failed — please try a smaller image.');
    }
    setUploading(false);
  };

  const validate = () => {
    if (!ease) return 'Please rate how easy ASAP was to understand.';
    if (whatFor.trim().length < 3) return 'Tell us what you think ASAP is for.';
    if (hadBugs === null) return 'Did you experience any bugs?';
    if (hadBugs && bugDesc.trim().length < 3) return 'Please describe the bug.';
    if (!useAgain) return 'Would you use ASAP again?';
    if (nps === null) return 'Pick your likelihood to recommend (0–10).';
    if (followUp === null) return 'Are you open to a follow-up chat?';
    if (followUp && followUpContact.trim().length < 4) return 'Please share an email or phone number for follow-up.';
    return null;
  };

  const submit = async () => {
    const v = validate();
    if (v) { setError(v); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setError('');
    setSubmitting(true);
    try {
      await api.post('/feedback/beta', {
        ease_of_understanding: ease,
        what_is_asap_for: whatFor.trim(),
        actions_completed: actions,
        most_confusing: confusing.trim(),
        had_bugs: hadBugs,
        bug_description: hadBugs ? bugDesc.trim() : '',
        bug_screenshot_url: hadBugs ? bugScreenshotUrl : '',
        favorite_feature: favorite,
        favorite_other: favorite === 'other' ? favOther.trim() : '',
        remove_or_improve: improve.trim(),
        would_use_again: useAgain,
        nps,
        final_thoughts: finalThoughts.trim(),
        follow_up_ok: followUp,
        follow_up_contact: followUp ? followUpContact.trim() : '',
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not submit. Please try again.');
    }
    setSubmitting(false);
  };

  // ── Success state ──────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8F7F2] flex flex-col" data-testid="feedback-success">
        <header className="sticky top-0 bg-[#F8F7F2]/95 backdrop-blur z-10 pt-safe-area">
          <div className="flex items-center gap-2 px-4 py-3">
            <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 active:scale-95" aria-label="Back" data-testid="back-btn">
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-base font-extrabold text-[#111]">Beta Feedback</h1>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-6 py-12 text-center">
          <div className="max-w-sm">
            <div className="w-16 h-16 rounded-full bg-[#B6E600]/20 text-[#3F5200] flex items-center justify-center mx-auto mb-4">
              <Check size={28} strokeWidth={3} />
            </div>
            <h2 className="text-2xl font-black text-[#111] mb-2">
              {alreadySubmitted ? 'Already received 🙏' : 'Thank you 🙏'}
            </h2>
            <p className="text-sm text-[#6B6E73] leading-relaxed mb-6">
              {alreadySubmitted
                ? "We've got your feedback from earlier. If you want to share more thoughts, ping the founders directly."
                : "Your feedback shapes the next version of ASAP. We read every single response."}
            </p>
            <button
              onClick={() => navigate('/home')}
              data-testid="back-home-btn"
              className="px-6 py-3 rounded-full bg-[#111] text-white font-bold text-sm active:scale-[0.98]"
            >
              Back to ASAP
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F8F7F2]" data-testid="beta-feedback-page">
      <header className="sticky top-0 bg-[#F8F7F2]/95 backdrop-blur z-10 border-b border-black/[0.04] pt-safe-area">
        <div className="flex items-center gap-2 px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 active:scale-95" aria-label="Back" data-testid="back-btn">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-extrabold text-[#111]">Beta Feedback</h1>
            <p className="text-[11px] text-[#9B9EA3]">2 mins · helps shape ASAP v1.0</p>
          </div>
        </div>
      </header>

      <main className="max-w-[640px] mx-auto px-4 py-5 space-y-7 pb-24">
        {error && (
          <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-2xl px-3 py-2.5 text-sm text-[#FF3B30] font-bold" data-testid="form-error">
            {error}
          </div>
        )}

        {/* Q1 — Ease of understanding */}
        <Q n={1} title="How easy was it to understand ASAP?" hint="1 = totally confused · 5 = crystal clear">
          <div className="flex gap-2" data-testid="q1-rating">
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                onClick={() => setEase(n)}
                data-testid={`ease-${n}`}
                className={`flex-1 py-3 rounded-2xl font-extrabold text-base active:scale-95 transition-colors ${
                  ease === n ? 'bg-[#111] text-white' : 'bg-white text-[#111] border border-black/[0.06]'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </Q>

        {/* Q2 — What is ASAP for */}
        <Q n={2} title="In your own words, what do you think ASAP is for?">
          <Textarea value={whatFor} onChange={setWhatFor} maxLength={2000} testId="q2-input"
            placeholder="e.g. Meeting new people through real-life plans" />
        </Q>

        {/* Q3 — Actions completed */}
        <Q n={3} title="Were you able to complete these actions?" hint="Tick all that apply">
          <div className="grid gap-2" data-testid="q3-actions">
            {ACTIONS.map(a => {
              const checked = actions.includes(a.key);
              return (
                <button
                  key={a.key}
                  onClick={() => toggleAction(a.key)}
                  data-testid={`action-${a.key}`}
                  className={`flex items-center gap-3 px-3 py-3 rounded-2xl border text-left transition-colors ${
                    checked ? 'bg-[#B6E600]/15 border-[#B6E600] text-[#3F5200]' : 'bg-white border-black/[0.06] text-[#111]'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${
                    checked ? 'bg-[#B6E600]' : 'border-2 border-black/15'
                  }`}>
                    {checked && <Check size={14} strokeWidth={3} className="text-[#3F5200]" />}
                  </div>
                  <span className="text-sm font-bold">{a.label}</span>
                </button>
              );
            })}
          </div>
        </Q>

        {/* Q4 — Most confusing */}
        <Q n={4} title="What confused you the most?" optional>
          <Textarea value={confusing} onChange={setConfusing} maxLength={2000} testId="q4-input"
            placeholder="e.g. Couldn't find the create-plan button" />
        </Q>

        {/* Q5 — Bugs */}
        <Q n={5} title="Did you experience any bugs?">
          <div className="grid grid-cols-2 gap-2" data-testid="q5-bugs">
            {[{ k: false, l: 'No' }, { k: true, l: 'Yes' }].map(o => (
              <button
                key={String(o.k)}
                onClick={() => setHadBugs(o.k)}
                data-testid={`bugs-${o.k ? 'yes' : 'no'}`}
                className={`py-3 rounded-2xl font-extrabold text-sm active:scale-95 ${
                  hadBugs === o.k ? 'bg-[#111] text-white' : 'bg-white text-[#111] border border-black/[0.06]'
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
          {hadBugs === true && (
            <div className="mt-3 space-y-3" data-testid="q5-yes-block">
              <Textarea value={bugDesc} onChange={setBugDesc} maxLength={3000} testId="bug-desc"
                placeholder="What happened? Steps to reproduce help us most." />
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleScreenshot}
                  className="hidden"
                  data-testid="screenshot-input"
                />
                {bugScreenshotUrl ? (
                  <div className="relative inline-block">
                    <img loading="lazy" decoding="async" src={bugScreenshotUrl} alt="Bug" className="rounded-2xl max-h-40 border border-black/[0.06]" />
                    <button
                      onClick={() => setBugScreenshotUrl('')}
                      data-testid="screenshot-remove"
                      className="absolute -top-2 -right-2 w-7 h-7 bg-[#111] text-white rounded-full flex items-center justify-center"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="screenshot-upload-btn"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-black/[0.06] text-sm font-bold text-[#111] active:scale-95 disabled:opacity-50"
                  >
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                    {uploading ? 'Uploading…' : 'Attach screenshot (optional)'}
                  </button>
                )}
              </div>
            </div>
          )}
        </Q>

        {/* Q6 — Favorite feature */}
        <Q n={6} title="What feature did you enjoy the most?" optional>
          <div className="flex flex-wrap gap-2" data-testid="q6-features">
            {FEATURES.map(f => (
              <button
                key={f.key}
                onClick={() => setFavorite(favorite === f.key ? '' : f.key)}
                data-testid={`fav-${f.key}`}
                className={`px-3.5 py-2 rounded-full text-sm font-bold border transition-colors ${
                  favorite === f.key
                    ? 'bg-[#111] text-white border-[#111]'
                    : 'bg-white text-[#111] border-black/[0.08]'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {favorite === 'other' && (
            <input
              value={favOther}
              onChange={e => setFavOther(e.target.value.slice(0, 400))}
              data-testid="fav-other-input"
              placeholder="Which feature?"
              className="mt-3 w-full bg-white border border-black/[0.06] rounded-2xl px-3 py-2.5 text-sm"
            />
          )}
        </Q>

        {/* Q7 — Remove/improve */}
        <Q n={7} title="What feature would you remove or improve?" optional>
          <Textarea value={improve} onChange={setImprove} maxLength={2000} testId="q7-input"
            placeholder="Be brutal — this is most useful for us" />
        </Q>

        {/* Q8 — Use again */}
        <Q n={8} title="Would you actually use ASAP again?">
          <div className="grid gap-2" data-testid="q8-use-again">
            {USE_AGAIN.map(u => (
              <button
                key={u.key}
                onClick={() => setUseAgain(u.key)}
                data-testid={`use-${u.key}`}
                className={`flex items-center gap-3 px-3 py-3 rounded-2xl border text-left active:scale-[0.99] transition-colors ${
                  useAgain === u.key
                    ? 'bg-[#B6E600]/15 border-[#B6E600] text-[#3F5200]'
                    : 'bg-white border-black/[0.06] text-[#111]'
                }`}
              >
                <span className="text-lg">{u.emoji}</span>
                <span className="text-sm font-bold">{u.label}</span>
              </button>
            ))}
          </div>
        </Q>

        {/* Q9 — NPS */}
        <Q n={9} title="How likely are you to recommend ASAP to a friend?" hint="0 = not at all · 10 = absolutely">
          <div className="grid grid-cols-11 gap-1" data-testid="q9-nps">
            {Array.from({ length: 11 }, (_, i) => i).map(i => (
              <button
                key={i}
                onClick={() => setNps(i)}
                data-testid={`nps-${i}`}
                className={`py-2.5 rounded-lg font-extrabold text-xs ${
                  nps === i ? 'bg-[#111] text-white' : 'bg-white text-[#111] border border-black/[0.06]'
                }`}
              >
                {i}
              </button>
            ))}
          </div>
        </Q>

        {/* Q10 — Final thoughts */}
        <Q n={10} title="Any final thoughts or suggestions?" optional>
          <Textarea value={finalThoughts} onChange={setFinalThoughts} maxLength={3000} testId="q10-input"
            placeholder="Anything we missed" />
        </Q>

        {/* Q11 — Follow-up */}
        <Q n={11} title="Open to a 10-minute follow-up chat?">
          <div className="grid grid-cols-2 gap-2" data-testid="q11-followup">
            {[{ k: true, l: 'Yes' }, { k: false, l: 'No' }].map(o => (
              <button
                key={String(o.k)}
                onClick={() => setFollowUp(o.k)}
                data-testid={`followup-${o.k ? 'yes' : 'no'}`}
                className={`py-3 rounded-2xl font-extrabold text-sm active:scale-95 ${
                  followUp === o.k ? 'bg-[#111] text-white' : 'bg-white text-[#111] border border-black/[0.06]'
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
          {followUp === true && (
            <input
              value={followUpContact}
              onChange={e => setFollowUpContact(e.target.value.slice(0, 200))}
              data-testid="followup-contact"
              placeholder="Email or phone"
              className="mt-3 w-full bg-white border border-black/[0.06] rounded-2xl px-3 py-2.5 text-sm"
            />
          )}
        </Q>

        <button
          onClick={submit}
          disabled={submitting}
          data-testid="submit-feedback"
          className="w-full py-4 rounded-full bg-[#B6E600] text-[#111] font-extrabold text-sm active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
          {submitting ? 'Sending…' : 'Send feedback'}
        </button>
        <p className="text-[11px] text-[#9B9EA3] text-center">
          We only use this to improve ASAP. Never shared.
        </p>
      </main>
    </div>
  );
}

// ── small helpers ──────────────────────────────────────────────────
function Q({ n, title, hint, optional, children }) {
  return (
    <section data-testid={`question-${n}`}>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-sm font-extrabold text-[#111] leading-snug">
          <span className="text-[#9B9EA3] font-bold mr-1.5">{n}.</span>{title}
        </h2>
        {optional && <span className="text-[10px] text-[#9B9EA3] font-bold uppercase tracking-wider">Optional</span>}
      </div>
      {hint && <p className="text-[11px] text-[#9B9EA3] mb-2">{hint}</p>}
      {children}
    </section>
  );
}

function Textarea({ value, onChange, maxLength, placeholder, testId }) {
  return (
    <div>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value.slice(0, maxLength))}
        placeholder={placeholder}
        rows={3}
        data-testid={testId}
        className="w-full bg-white border border-black/[0.06] rounded-2xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-[#111]/30"
      />
      <p className="text-[10px] text-[#9B9EA3] text-right mt-1">{value.length}/{maxLength}</p>
    </div>
  );
}
